import Quotation from "assets/print-templates/quotation.jpg";
import { numberFormatAsString } from "commons/ultilities";
import { dateTimeDisplay } from "commons/ultilities/date/dayjs";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useMemo } from "react";
import { ElementToPrint } from "share-components/templates/print/element";
import { PreviewPrinting } from "share-components/templates/print/print-preview";
export const QuotationPrintPreview = observer(() => {
  const {
    quotationManagement: { openPrintPreview, setProp, quotationDetail },
  } = useStores();
  const onCloseHandler = useCallback(() => {
    setProp("openPrintPreview", false);
  }, [setProp]);

  const dateElm = useMemo(() => {
    const now = dayjs().format();
    const dateElm: ElementToPrint = {
      value: dateTimeDisplay(now),
      left: "21.2cm",
      top: "1.6cm",
      name: "date",
      width: "6cm",
    };
    return [dateElm];
  }, []);

  const quotationElm = useMemo(() => {
    const quotationElm: ElementToPrint[] = [
      {
        name: "numer",
        value: quotationDetail?.number || "",
        left: "21.1cm",
        top: "2.2cm",
        width: "6cm",
      },
      {
        name: "customerName",
        value: quotationDetail?.customer?.name || "",
        left: "5.1cm",
        top: "2.9cm",
        width: "11.5cm",
      },
      {
        name: "customerHeadOffice",
        value: quotationDetail?.customer?.headOfficeAddress || "",
        left: "5.1cm",
        top: "3.5cm",
        width: "11.5cm",
      },
      {
        name: "address",
        value: quotationDetail?.deliveryAddress?.address || "",
        left: "5.1cm",
        top: "4.1cm",
        width: "11.5cm",
      },
      {
        name: "reciver",
        value: quotationDetail?.receiverInfo || "",
        left: "19.7cm",
        top: "2.9cm",
        width: "8.5cm",
      },

      {
        name: "note",
        value: quotationDetail?.note || "",
        left: "2.8cm",
        top: "15cm",
        width: "25.3cm",
      },
    ];
    return quotationElm;
  }, [
    quotationDetail?.customer?.headOfficeAddress,
    quotationDetail?.customer?.name,
    quotationDetail?.deliveryAddress?.address,
    quotationDetail?.note,
    quotationDetail?.number,
    quotationDetail?.receiverInfo,
  ]);

  const quotationItemElms = useMemo(() => {
    if (!quotationDetail?.items) {
      return [];
    }
    const quotationItems: ElementToPrint[] = [];
    const firstTop = 6.77;
    quotationDetail?.items.forEach((x, idx) => {
      const top = `${firstTop + idx * 0.95}cm`;
      const fontSize = "12px";
      const height = "29px";
      const ordinalNumberElm: ElementToPrint = {
        value: (idx + 1).toString(),
        left: "0.3cm",
        top: top,
        name: "ordinalNumber",
        fontSize: fontSize,
        width: "0.4cm",
        height: height,
        textAlight: "center",
      };
      quotationItems.push(ordinalNumberElm);
      const productNameElm: ElementToPrint = {
        name: "productName",
        left: "0.9cm",
        top: top,
        value: x.product?.name || "",
        fontSize: fontSize,
        width: "4.7cm",
        height: height,
        textWrap: "wrap",
        lineClamp: "2",
        lineHeight: "normal",
      };
      quotationItems.push(productNameElm);
      const productCodeElm: ElementToPrint = {
        name: "productCode",
        left: "5.8cm",
        top: top,
        value: x.product?.code || "",
        fontSize: fontSize,
        width: "1.9cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(productCodeElm);

      const barcodeElm: ElementToPrint = {
        name: "barcode",
        left: "7.9cm",
        top: top,
        value: x.product?.barcode || "",
        fontSize: fontSize,
        width: "1.9cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(barcodeElm);

      const paperTypeElm: ElementToPrint = {
        name: "paperType",
        left: "10.1cm",
        top: top,
        value: x.product?.material?.paperType || "",
        fontSize: fontSize,
        width: "1.3cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(paperTypeElm);

      const gsmElm: ElementToPrint = {
        name: "gsm",
        left: "11.5cm",
        top: top,
        value:
          numberFormatAsString(
            x.product?.material?.gsmStandard?.toString() || ""
          ) || "",
        fontSize: fontSize,
        width: "1cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(gsmElm);

      const lengthElm: ElementToPrint = {
        name: "length",
        left: "12.6cm",
        top: top,
        value:
          numberFormatAsString(
            x.product?.dimensionWeight?.lengthStandard?.toString() || ""
          ) || "",
        fontSize: fontSize,
        width: "1cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(lengthElm);

      const widthElm: ElementToPrint = {
        name: "width",
        left: "13.7cm",
        top: top,
        value:
          numberFormatAsString(
            x.product?.dimensionWeight?.widthStandard?.toString() || ""
          ) || "",
        fontSize: fontSize,
        width: "1cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(widthElm);

      const heightElm: ElementToPrint = {
        name: "height",
        left: "14.8cm",
        top: top,
        value:
          numberFormatAsString(
            x.product?.dimensionWeight?.heightStandard?.toString() || ""
          ) || "",
        fontSize: fontSize,
        width: "1cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(heightElm);

      const frontColorElm: ElementToPrint = {
        name: "frontColor",
        left: "16cm",
        top: top,
        value:
          numberFormatAsString(
            x.product?.process?.frontPrintColor?.toString() || ""
          ) || "",
        fontSize: fontSize,
        width: "0.8cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(frontColorElm);

      const backColorElm: ElementToPrint = {
        name: "backColor",
        left: "16.9cm",
        top: top,
        value:
          numberFormatAsString(
            x.product?.process?.backPrintColor?.toString() || ""
          ) || "",
        fontSize: fontSize,
        width: "0.8cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(backColorElm);

      const otherProcessVal = `${
        x.product?.process?.frontPrintMatte &&
        x.product?.process?.frontPrintMatte.toString().trim() !== "-"
          ? `${x.product?.process?.frontPrintMatte.toString()} -`
          : ""
      }${
        x.product?.process?.backPrintMatte &&
        x.product?.process?.backPrintMatte.toString().trim() !== "-"
          ? `${x.product?.process?.backPrintMatte.toString()} -`
          : ""
      }${
        x.product?.process?.otherHandle &&
        x.product?.process?.otherHandle.toString().trim() !== "-"
          ? `${x.product?.process?.otherHandle.toString()} -`
          : ""
      }`;

      const otherProcessElm: ElementToPrint = {
        name: "otherProcess",
        left: "17.9cm",
        top: top,
        value: otherProcessVal.slice(0, -1),
        fontSize: "8px",
        width: "1.8cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(otherProcessElm);

      const detailProccessValue = `${
        x.product?.process?.frontPrintHandleWay &&
        x.product?.process?.frontPrintHandleWay.toString().trim() !== "-"
          ? `${x.product?.process?.frontPrintHandleWay.toString()} -`
          : ""
      }${
        x.product?.process?.backPrintHandleWay &&
        x.product?.process?.backPrintHandleWay.toString().trim() !== "-"
          ? `${x.product?.process?.backPrintHandleWay.toString()} -`
          : ""
      }`;
      const detailProcessElm: ElementToPrint = {
        name: "handleDetail",
        left: "19.8cm",
        top: top,
        value: detailProccessValue?.slice(0, -1) || "",
        fontSize: "8px",
        width: "1.2cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(detailProcessElm);

      const amountElm: ElementToPrint = {
        name: "amount",
        left: "21.2cm",
        top: top,
        value: numberFormatAsString(x.amount?.toString() || ""),
        fontSize: fontSize,
        width: "1.4cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(amountElm);

      const unitPriceElm: ElementToPrint = {
        name: "unitPrice",
        left: "22.8cm",
        top: top,
        value: numberFormatAsString(x.unitPrice?.toString() || ""),
        fontSize: fontSize,
        width: "1.2cm",
        height: height,
        textWrap: "wrap",
      };
      quotationItems.push(unitPriceElm);

      const noteElm: ElementToPrint = {
        name: "note",
        left: "24.2cm",
        top: top,
        value: x.note?.toString() || "",
        fontSize: fontSize,
        height: "35px",
        textWrap: "wrap",
        width: "4.2cm",
        lineClamp: "2",
      };
      quotationItems.push(noteElm);
    });
    return quotationItems;
  }, [quotationDetail?.items]);

  const elements = useMemo(() => {
    return [...dateElm, ...quotationElm, ...quotationItemElms];
  }, [dateElm, quotationElm, quotationItemElms]);
  return (
    <PreviewPrinting
      open={openPrintPreview}
      printIncludeTemplate={true}
      elements={elements}
      templateImage={Quotation}
      mode="landscape"
      fontSize="13px"
      paperSize={{
        height: "21cm",
        width: "29.7cm",
      }}
      paperPadding="0.5cm"
      onClose={onCloseHandler}
    />
  );
});
